import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { Toaster } from 'react-hot-toast';
import { Toaster as SonnerToaster } from 'sonner';
import '@/App.css';

// PWA Components
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { registerServiceWorker } from './utils/pwa';

// Voice Announcer (plays on all pages)
import VoiceAnnouncer from './components/VoiceAnnouncer';

// Soundbox Announcer (Wave 46 · plays ATC voice alerts on all pages, not just /atc)
import SoundboxAnnouncer from './components/soundbox/SoundboxAnnouncer';
import SoundboxWidget from './components/soundbox/SoundboxWidget';
import HiddenToolsStyleInjector from './components/HiddenToolsStyleInjector';

// Global Alarm Scheduler (fires SuperTool task reminders site-wide)
import AlarmScheduler from './components/AlarmScheduler';

// Public Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import ShopCheckout from './pages/ShopCheckout';
import OrderSuccess from './pages/OrderSuccess';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import PublicCard from './pages/PublicCard';
import TalkToPriya from './pages/TalkToPriya';
import AudioGuide from './pages/AudioGuide';
import PublicLinkBio from './pages/PublicLinkBio';
import P2PDeals from './pages/P2PDeals';
import AuctionHouse from './pages/AuctionHouse';
import BuyPass from './pages/p2p/BuyPass';
import CreateListing from './pages/p2p/CreateListing';
import CreateRealEstateListing from './pages/user/CreateRealEstateListing';
import FranchisePage from './pages/FranchisePage';

// Game Module Pages
import AGPWallet from './pages/AGPWallet';
import GameZone from './pages/GameZone';
import GamePlayer from './pages/GamePlayer';
import ChessGame from './pages/games/ChessGame';
import SimpleChess from './pages/games/SimpleChess';
import JumpHeroGame from './pages/games/JumpHeroGame';
import MatchManiaGame from './pages/games/MatchManiaGame';
import RushRunnerGame from './pages/games/RushRunnerGame';

// AI Marketing Tool
import MarketingDashboard from './pages/marketing/MarketingDashboard';

// Free Tools
import FreeTools from './pages/tools/FreeTools';
import WhatsAppLinkGenerator from './pages/tools/WhatsAppLinkGenerator';
import InvoiceGenerator from './pages/tools/InvoiceGenerator';
import TestimonialCollector from './pages/tools/TestimonialCollector';
import QRCodeGenerator from './pages/tools/QRCodeGenerator';
import BioLinkGenerator from './pages/tools/BioLinkGenerator';
import EMICalculator from './pages/tools/EMICalculator';
import BusinessCardGenerator from './pages/tools/BusinessCardGenerator';
import VirtualTourCreator from './pages/tools/VirtualTourCreator';
import AIVideoAdCreator from './pages/tools/AIVideoAdCreator';

// Display TV Mode
import DisplayTV from './pages/display/DisplayTV';

// User Pages
import UserDashboard from './pages/user/Dashboard';
import LeadDashboard from './pages/user/LeadDashboard';
import UserOrders from './pages/user/Orders';
import UserProducts from './pages/user/Products';
import UserCourses from './pages/user/Courses';
import UserProfile from './pages/user/Profile';
import TransactionHistory from './pages/user/TransactionHistory';
import ActivationPacks from './pages/user/ActivationPacks';
import Commissions from './pages/user/Commissions';
import Matrix from './pages/user/Matrix';
import TeamReport from './pages/user/TeamReport';
import Wallets from './pages/user/Wallets';
import ATDepositPage from './pages/user/ATDepositPage';
import Ranks from './pages/user/Ranks';
import MyShops from './pages/user/MyShops';
import MyShopEditor from './pages/user/MyShopEditor';
import LinkInBio from './pages/user/LinkInBio';
import PassInventory from './pages/user/PassInventory';
import Helpdesk from './pages/user/Helpdesk';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ToolsAnalytics from './pages/admin/ToolsAnalytics';
import AIToolsAdmin from './pages/admin/AIToolsAdmin';
import AIControlPanel from './pages/admin/AIControlPanel';
import AIUsageAdmin from './pages/admin/AIUsageAdmin';
import SupportAdmin from './pages/admin/SupportAdmin';
import P2PAdmin from './pages/admin/P2PAdmin';
import FranchiseAdmin from './pages/admin/FranchiseAdmin';
import PassAdmin from './pages/admin/PassAdmin';
import AdminProducts from './pages/admin/Products';
import AdminCourses from './pages/admin/Courses';
import AdminOrders from './pages/admin/Orders';
import AdminMarketplaceOrders from './pages/admin/AdminMarketplaceOrders';
import AdminMarketplaceReviews from './pages/admin/AdminMarketplaceReviews';
import AdminDeliveryPartners from './pages/admin/AdminDeliveryPartners';
import AdminSmartPanel from './pages/admin/AdminSmartPanel';
import AdminATC from './pages/admin/AdminATC';
import AdminCommissionLedger from './pages/admin/AdminCommissionLedger';
import AdminSubAdmins from './pages/admin/AdminSubAdmins';
import AdminActivationKeys from './pages/admin/AdminActivationKeys';
import AdminPlatformSettings from './pages/admin/AdminPlatformSettings';
import AdminUserTools from './pages/admin/AdminUserTools';
import AdminAutoApproveRules from './pages/admin/AdminAutoApproveRules';
import AdminPlatformContent from './pages/admin/AdminPlatformContent';
import AdminActivityLog from './pages/admin/AdminActivityLog';
import RedeemKey from './pages/RedeemKey';
import AdminTaxWallet from './pages/admin/AdminTaxWallet';
import NotFound from './pages/NotFound';
import AdminPages from './pages/admin/Pages';
import AdminSettings from './pages/admin/Settings';
import AdminUsers from './pages/admin/Users';
import AdminFundRequests from './pages/admin/FundRequests';
import AdminTestimonials from './pages/admin/Testimonials';
import AdminBroadcasts from './pages/admin/Broadcasts';
import Admin2FA from './pages/admin/TwoFactorAuth';
import AdminDisplayWindows from './pages/admin/DisplayWindows';
import AdminPopups from './pages/admin/Popups';
import AdminActivationPacks from './pages/admin/ActivationPacks';
import AdminImportantLinks from './pages/admin/ImportantLinks';
import AdminSystemWallets from './pages/admin/SystemWallets';
import AdminCustomPages from './pages/admin/CustomPages';
import AdminRankCompetitions from './pages/admin/RankCompetitions';
import AdminPageBuilder from './pages/admin/PageBuilder';
import AdminPageEditor from './pages/admin/PageEditor';
import AdminUserPagePermissions from './pages/admin/UserPagePermissions';
import AdminCustomTabs from './pages/admin/CustomTabs';
import AdminShopBuilder from './pages/admin/ShopBuilder';
import AdminShopEditor from './pages/admin/ShopEditor';
import TemplatePermissions from './pages/admin/TemplatePermissions';
import AdminVoiceAgent from './pages/admin/VoiceAgentAdmin';
import AdminMagicQR from './pages/admin/MagicQRAdmin';
import AdminWhatsAppBot from './pages/admin/WhatsAppBotAdmin';
import AdminRanksRewards from './pages/admin/RanksRewardsAdmin';
import AdminAffiliate from './pages/admin/AffiliateAdmin';
import AdminAuction from './pages/admin/AuctionAdmin';
import TransactionLedger from './pages/admin/TransactionLedger';
import FreeToolsAdmin from './pages/admin/FreeToolsAdmin';
import AuctionRoom from './pages/admin/AuctionRoom';
import AdminPaymentSettings from './pages/admin/PaymentSettings';
import AdminPushNotifications from './pages/admin/PushNotifications';
import AdminWhatsAppLeads from './pages/admin/WhatsAppLeads';
import AdminP2PCoupons from './pages/admin/P2PCoupons';
import AdminP2PSettings from './pages/admin/P2PSettings';
import VoiceControlPanel from './pages/admin/VoiceControlPanel';
import DVCTemplateAdmin from './pages/admin/DVCTemplateAdmin';
import ShopTemplateAdmin from './pages/admin/ShopTemplateAdmin';

// Game Module Admin Pages
import GameManager from './pages/admin/GameManager';
import GameSystemWallets from './pages/admin/GameSystemWallets';
import BioDataAdmin from './pages/admin/BioDataAdmin';
import HotNewsAdmin from './pages/admin/HotNewsAdmin';
import SankatMochanAdmin from './pages/admin/SankatMochanAdmin';
import SankatVIPPanel from './pages/admin/SankatVIPPanel';
import AnantFeelingsAdmin from './pages/admin/AnantFeelingsAdmin';
import AcademyQuizAdmin from './pages/admin/AcademyQuizAdmin';
import MarketingAdmin from './pages/admin/MarketingAdmin';
import ShareAnalytics from './pages/admin/ShareAnalytics';
import SuperToolAdmin from './pages/admin/SuperToolAdmin';
import QRLeadAdmin from './pages/admin/QRLeadAdmin';
import AVXAdmin from './pages/admin/AVXAdmin';
import TSBAdmin from './pages/admin/TSBAdmin';

// Sankat Mochan Samadhan
import SankatMochanSamadhan from './pages/SankatMochanSamadhan';

// User Pages
import DigitalCard from './pages/user/DigitalCard';
import MyMagicQR from './pages/user/MyMagicQR';
import ShopAnalytics from './pages/user/ShopAnalytics';
import AffiliateMarketplace from './pages/user/AffiliateMarketplace';
import NotificationCenter from './pages/user/NotificationCenter';
import MyRanks from './pages/user/MyRanks';
import BioDataBuilder from './pages/user/BioDataBuilder';
import SuperTool from './pages/user/SuperTool';
import QRLeadCollector from './pages/user/QRLeadCollector';
import AVX from './pages/avx/AVX';
import TSB from './pages/tsb/TSB';
import Vendor from './pages/tsb/Vendor';
import Explorer from './pages/tsb/Explorer';
import HolderHistory from './pages/tsb/HolderHistory';

// Universal SaaS Auto-Trading Platform
import AlgoTrading from './pages/algotrading/AlgoTrading';
import AlgoTradingAdmin from './pages/admin/AlgoTradingAdmin';

// Anant Inner Self Guidance — Osho Zen Tarot
import AnantTarot from './pages/anant_tarot/AnantTarot';
import TarotGallery from './pages/anant_tarot/TarotGallery';
import TarotCardDetail from './pages/anant_tarot/TarotCardDetail';
import TarotPublicReading from './pages/anant_tarot/TarotPublicReading';
import AnantFeelings from './pages/anant_feelings/AnantFeelings';
import SankatPaidSession from './pages/sankat_mochan/SankatPaidSession';
import AnantTarotAdmin from './pages/admin/AnantTarotAdmin';

// Trading Education Hub + AI Tutor
import Education from './pages/education/Education';

// Smart Business Card
import SmartCardPublic from './pages/smart_card/SmartCardPublic';
import { SmartCardManager, SmartCardEditor } from './pages/smart_card/SmartCardManager';

// Freedom Academy
import { Academy, AcademyCourse } from './pages/academy/Academy';
import AcademyCertVerify from './pages/academy/AcademyCertVerify';
// Anant Vyapar (CRM)
import AnantVyapar from './pages/vyapar/AnantVyapar';
import VyaparMagicQR from './pages/vyapar/VyaparMagicQR';
import VyaparAdmin from './pages/vyapar/VyaparAdmin';
// Anant Safety QR
import AnantSafety from './pages/safety/AnantSafety';
import APTS from './pages/apts/APTS';
import APTSAdmin from './pages/apts/APTSAdmin';
import ASST from './pages/asst/ASST';
import ASSTAdmin from './pages/asst/ASSTAdmin';
import SamadhanCenter from './pages/samadhan/SamadhanCenter';
import SamadhanVision from './pages/samadhan/SamadhanVision';
import SamadhanGallery from './pages/samadhan/SamadhanGallery';
import SamadhanAdmin from './pages/samadhan/SamadhanAdmin';
import TrialRoom from './pages/trial_room/TrialRoom';
import TrialRoomAdmin from './pages/trial_room/TrialRoomAdmin';
import AdminNewTools from './pages/admin/AdminNewTools';
import BrandMark from './pages/brandmark/BrandMark';
import LeadGen from './pages/leadgen/LeadGen';
import LeadGenPublicLP from './pages/leadgen/LeadGenPublicLP';
import Corpus50 from './pages/corpus50/Corpus50';
import SmartVault from './pages/vault/SmartVault';
import AnantYojana from './pages/anant_yojana/AnantYojana';
import AnantKhata from './pages/khata/AnantKhata';
import AnantSamachar from './pages/asn/AnantSamachar';
import AnantSamacharArticle from './pages/asn/AnantSamacharArticle';
import AnantHealthTracker from './pages/health/AnantHealthTracker';
import AnantFieldPro from './pages/fieldpro/AnantFieldPro';
import AnantCityPro from './pages/citypro/AnantCityPro';
import CityProShowcase from './pages/citypro/CityProShowcase';
import CityProDelivery from './pages/citypro/CityProDelivery';
import CityProDeliveryRegister from './pages/citypro/CityProDeliveryRegister';
import CityProLeaderboard from './pages/citypro/CityProLeaderboard';
import AnantSmartMarketing from './pages/asm/AnantSmartMarketing';
import AnantSmartMarketingPublic from './pages/asm/AnantSmartMarketingPublic';
import AnantVyapaarMaster from './pages/vyapaar_master/AnantVyapaarMaster';
import AVMPublicCard from './pages/vyapaar_master/AVMPublicCard';
import AnantSmartFlyer from './pages/flyer/AnantSmartFlyer';
import FlyerPublicView from './pages/flyer/FlyerPublicView';
import AnantSocialShare from './pages/social_share/AnantSocialShare';
import AnantWebShop from './pages/webshop/AnantWebShop';
import AnantMarketplace from './pages/marketplace/AnantMarketplace';
import AnantTradeCenter from './pages/atc/AnantTradeCenter';
import AtcB2C from './pages/atc/AtcB2C';
import PublicAtcPost from './pages/atc/PublicAtcPost';
import LaunchParty from './pages/atc/LaunchParty';
import FounderWall from './pages/atc/FounderWall';
import AtcB2B from './pages/atc/AtcB2B';
import OrderTrackPublic from './pages/marketplace/OrderTrackPublic';
import GroupBuyLanding from './pages/marketplace/GroupBuyLanding';
import FranchiseeLanding from './pages/FranchiseeLanding';
import DeliveryPartnerDashboard from './pages/delivery/DeliveryPartnerDashboard';
import AdminShell from './components/admin/AdminShell';
import PublicMarketplaceStore from './pages/PublicMarketplaceStore';
import PublicStoreVideos from './pages/PublicStoreVideos';
import PublicWebShop from './pages/PublicWebShop';
import SystemAuditor from './pages/admin/SystemAuditor';
import AnantSamacharAllNews from './pages/asn/AnantSamacharAllNews';
import SafetyQRScan from './pages/safety/SafetyQRScan';
import NetworkLive from './pages/NetworkLive';
import NetworkLiveWidget from './pages/NetworkLiveWidget';
import SafetyQRAdmin from './pages/safety/SafetyQRAdmin';

// Unified Admin Console
import AnantAdminConsole from './pages/admin/AnantAdminConsole';
import AdminOpsDashboard from './pages/AdminOpsDashboard';

// Anant Display Engine
import DisplayBoard from './pages/DisplayBoard';
import DisplayEngineAdmin from './pages/admin/DisplayEngineAdmin';

// P2P Pages
import ListingDetail from './pages/ListingDetail';
import AuctionDetail from './pages/AuctionDetail';

// Public Pages (Dynamic)
import PublicPage from './pages/PublicPage';
import PublicShop from './pages/PublicShop';
import PublicReview from './pages/PublicReview';
import PublicBioData from './pages/PublicBioData';
import ShopTemplatesShowcase from './pages/ShopTemplatesShowcase';

// Layout Components
import Navbar from './components/Navbar';
import AnniversaryAutoPush from './components/AnniversaryAutoPush';
import SamadhanLiveTicker from './components/SamadhanLiveTicker';
import SamadhanLiveToast from './components/SamadhanLiveToast';
import Footer from './components/Footer';
import WhatsAppWidget from './components/WhatsAppWidget';
import VisitorCounter from './components/VisitorCounter';
import SoundPermissionBanner from './components/SoundPermissionBanner';
import AttractionPopup from './components/AttractionPopup';
import VoiceAgentWidget from './components/VoiceAgentWidget';

// Support fix 2026-09-24: /atc -> /atc/b2c without losing the query string (shared links)
const AtcRedirect = () => <Navigate to={`/atc/b2c${window.location.search}`} replace />;

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-2xl">Loading...</div>
    </div>;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-2xl">Loading...</div>
    </div>;
  }
  
  if (!(user && isAdmin)) return <Navigate to="/" />;
  return <AdminShell>{children}</AdminShell>;
};

// One-time LeadGen Refer & Earn auto-claim on login
const ReferralAutoClaim = () => {
  const { user } = useAuth();
  React.useEffect(() => {
    if (!user) return;
    const slug = (() => { try { return localStorage.getItem('leadgen_ref_slug'); } catch { return null; } })();
    if (!slug) return;
    if (localStorage.getItem('leadgen_ref_claimed') === '1') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leadgen/refer/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug }),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        try { localStorage.setItem('leadgen_ref_claimed', '1'); localStorage.removeItem('leadgen_ref_slug'); } catch (_) { /* ignore */ }
        // Best-effort toast if sonner available
        try { const { toast } = await import('sonner'); toast.success(data.message || '🎉 Refer bonus credited!'); } catch (_) { /* ignore */ }
      } else if (r.status === 409 || r.status === 410 || r.status === 400) {
        // Already claimed, expired, or self-refer — stop retrying
        try { localStorage.setItem('leadgen_ref_claimed', '1'); localStorage.removeItem('leadgen_ref_slug'); } catch (_) { /* ignore */ }
      }
    }).catch(() => { /* silent */ });
  }, [user]);
  return null;
};

const Layout = ({ children }) => {
  // Register service worker on app load
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <div className="App dark">
      <HiddenToolsStyleInjector />
      <Navbar />
      <AnniversaryAutoPush />
      <main className="min-h-screen pt-20">{children}</main>
      <Footer />
      <WhatsAppWidget />
      <VisitorCounter />
      <SoundPermissionBanner />
      <AttractionPopup page="home" />
      <PWAInstallPrompt />
      <VoiceAgentWidget />
      <VoiceAnnouncer />
      <SoundboxAnnouncer />
      <SoundboxWidget />
      <AlarmScheduler />
      <SamadhanLiveToast />
    </div>
  );
};

function AppContent() {
  // Global scroll direction tracker — tags <body> with data-scroll="down|up"
  // so tools' sticky bars can hide-on-scroll-down via CSS
  useEffect(() => {
    let lastY = 0; let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        if (Math.abs(y - lastY) > 8) {
          document.body.dataset.scroll = y > lastY && y > 80 ? 'down' : 'up';
          lastY = y;
        }
        ticking = false;
      });
      ticking = true;
    };
    document.body.dataset.scroll = 'up';
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <CartProvider>
          <LanguageProvider>
            <BrowserRouter>
              <ReferralAutoClaim />
              <Routes>
                {/* Display TV Mode - Full Screen without Layout */}
                <Route path="/display/:type" element={<DisplayTV />} />
                <Route path="/display" element={<DisplayTV />} />

                {/* Smart Business Card public viewer - Standalone (no app chrome) */}
                <Route path="/smart/:slug" element={<SmartCardPublic />} />

                {/* Anant Feelings — Direct Dil Se · standalone immersive (no app chrome) */}
                <Route path="/feelings" element={<PrivateRoute><AnantFeelings /></PrivateRoute>} />

                {/* Sankat Mochan paid 10-min consultation · standalone immersive */}
                <Route path="/sankat/paid" element={<PrivateRoute><SankatPaidSession /></PrivateRoute>} />

                {/* Public order tracking (standalone, no layout — shareable) */}
                <Route path="/order/:orderId" element={<OrderTrackPublic />} />

                {/* Public Group Buy landing (shareable link) */}
                <Route path="/gb/:gid" element={<GroupBuyLanding />} />

                {/* Public Franchisee landing (shareable link) */}
                <Route path="/franchisee/:pid" element={<FranchiseeLanding />} />

                {/* Delivery Partner dashboard (registration + assignments) */}
                <Route path="/delivery-partner" element={<PrivateRoute><DeliveryPartnerDashboard /></PrivateRoute>} />
                
                {/* All other routes with Layout */}
                <Route path="/*" element={
                  <Layout>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<Home />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/products/:id" element={<ProductDetail />} />
                      <Route path="/courses" element={<Courses />} />
                      <Route path="/courses/:id" element={<CourseDetail />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-failed" element={<PaymentFailed />} />
                      <Route path="/order-success" element={<OrderSuccess />} />
                      <Route path="/order-cancel" element={<PaymentFailed />} />
                      <Route path="/shop-checkout" element={<ShopCheckout />} />
                      <Route path="/card/:username" element={<PublicCard />} />
                      <Route path="/shop-templates" element={<ShopTemplatesShowcase />} />
                      
                      {/* P2P Deals & Auction Routes */}
                      <Route path="/deals" element={<P2PDeals />} />
                      <Route path="/deals/buy-pass" element={<PrivateRoute><BuyPass /></PrivateRoute>} />
                      <Route path="/deals/create" element={<PrivateRoute><CreateListing /></PrivateRoute>} />
                      <Route path="/deals/create/real-estate" element={<PrivateRoute><CreateRealEstateListing /></PrivateRoute>} />
                      <Route path="/deals/:id" element={<ListingDetail />} />
                      <Route path="/auctions" element={<AuctionHouse />} />
                      <Route path="/auction/:id" element={<AuctionDetail />} />
                      <Route path="/franchise" element={<FranchisePage />} />
                  
                  {/* Game Module Routes */}
                  <Route path="/games" element={<GameZone />} />
                  <Route path="/games/chess" element={<SimpleChess />} />
                  <Route path="/games/chess-master/play" element={<SimpleChess />} />
                  <Route path="/games/jump-hero/play" element={<PrivateRoute><JumpHeroGame /></PrivateRoute>} />
                  <Route path="/games/match-mania/play" element={<PrivateRoute><MatchManiaGame /></PrivateRoute>} />
                  <Route path="/games/rush-runner/play" element={<PrivateRoute><RushRunnerGame /></PrivateRoute>} />
                  <Route path="/games/:slug/play" element={<PrivateRoute><GamePlayer /></PrivateRoute>} />
                  <Route path="/agp-wallet" element={<PrivateRoute><AGPWallet /></PrivateRoute>} />
                  
                  {/* AI Marketing Tool */}
                  <Route path="/marketing" element={<MarketingDashboard />} />
                  
                  {/* Free Tools Routes */}
                  <Route path="/tools" element={<FreeTools />} />
                  <Route path="/tools/whatsapp-link" element={<WhatsAppLinkGenerator />} />
                  <Route path="/tools/invoice" element={<InvoiceGenerator />} />
                  <Route path="/tools/testimonials" element={<TestimonialCollector />} />
                  <Route path="/tools/qr-code" element={<QRCodeGenerator />} />
                  <Route path="/tools/bio-link" element={<BioLinkGenerator />} />
                  <Route path="/tools/emi-calculator" element={<EMICalculator />} />
                  <Route path="/tools/business-card" element={<BusinessCardGenerator />} />
                  <Route path="/tools/virtual-tour" element={<PrivateRoute><VirtualTourCreator /></PrivateRoute>} />
                  <Route path="/tools/video-ad" element={<PrivateRoute><AIVideoAdCreator /></PrivateRoute>} />
                  
                  <Route path="/talk-to-priya" element={<TalkToPriya />} />
                  <Route path="/audio-guide" element={<AudioGuide />} />
                  <Route path="/link/:username" element={<PublicLinkBio />} />
                  
                  {/* User Routes */}
                  <Route path="/dashboard" element={<PrivateRoute><UserDashboard /></PrivateRoute>} />
                  <Route path="/my-leads" element={<PrivateRoute><LeadDashboard /></PrivateRoute>} />
                  <Route path="/my-orders" element={<PrivateRoute><UserOrders /></PrivateRoute>} />
                  <Route path="/my-products" element={<PrivateRoute><UserProducts /></PrivateRoute>} />
                  <Route path="/my-courses" element={<PrivateRoute><UserCourses /></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
                  <Route path="/my-passes" element={<PrivateRoute><PassInventory /></PrivateRoute>} />
                  <Route path="/transactions" element={<PrivateRoute><TransactionHistory /></PrivateRoute>} />
                  <Route path="/activation" element={<PrivateRoute><ActivationPacks /></PrivateRoute>} />
                  <Route path="/commissions" element={<PrivateRoute><Commissions /></PrivateRoute>} />
                  <Route path="/matrix" element={<PrivateRoute><Matrix /></PrivateRoute>} />
                  <Route path="/team-report" element={<PrivateRoute><TeamReport /></PrivateRoute>} />
                  <Route path="/wallets" element={<PrivateRoute><Wallets /></PrivateRoute>} />
                  <Route path="/at-deposit" element={<PrivateRoute><ATDepositPage /></PrivateRoute>} />
                  <Route path="/ranks" element={<PrivateRoute><Ranks /></PrivateRoute>} />
                  <Route path="/my-card" element={<PrivateRoute><DigitalCard /></PrivateRoute>} />
                  <Route path="/digital-card" element={<PrivateRoute><DigitalCard /></PrivateRoute>} />
                  <Route path="/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
                  
                  {/* Member Shop Routes */}
                  <Route path="/my-shops" element={<PrivateRoute><MyShops /></PrivateRoute>} />
                  <Route path="/my-shops/:shopId/edit" element={<PrivateRoute><MyShopEditor /></PrivateRoute>} />
                  <Route path="/my-shops/:shopId/analytics" element={<PrivateRoute><ShopAnalytics /></PrivateRoute>} />
                  
                  {/* Magic QR Routes */}
                  <Route path="/my-magic-qr" element={<PrivateRoute><MyMagicQR /></PrivateRoute>} />
                  
                  {/* Affiliate Marketplace */}
                  <Route path="/affiliate" element={<PrivateRoute><AffiliateMarketplace /></PrivateRoute>} />
                  
                  {/* Notifications */}
                  <Route path="/notifications" element={<PrivateRoute><NotificationCenter /></PrivateRoute>} />
                  
                  {/* My Ranks & Rewards */}
                  <Route path="/my-ranks" element={<PrivateRoute><MyRanks /></PrivateRoute>} />
                  
                  {/* Link in Bio */}
                  <Route path="/my-link-bio" element={<PrivateRoute><LinkInBio /></PrivateRoute>} />
                  
                  {/* Bio Data Builder */}
                  <Route path="/biodata-builder" element={<PrivateRoute><BioDataBuilder /></PrivateRoute>} />
                  
                  {/* Sankat Mochan Samadhan */}
                  <Route path="/sankat-mochan" element={<SankatMochanSamadhan />} />
                  
                  {/* Helpdesk - FREE Support */}
                  <Route path="/helpdesk" element={<PrivateRoute><Helpdesk /></PrivateRoute>} />
                  
                  {/* SuperTool - Account Book + Calendar */}
                  <Route path="/supertool" element={<PrivateRoute><SuperTool /></PrivateRoute>} />
                  
                  {/* QR Lead Collector */}
                  <Route path="/qr-leads" element={<PrivateRoute><QRLeadCollector /></PrivateRoute>} />

                  {/* AVX - Anant Virtual Exchange (Fantasy Trading) */}
                  <Route path="/avx" element={<PrivateRoute><AVX /></PrivateRoute>} />
                  <Route path="/tsb" element={<PrivateRoute><TSB /></PrivateRoute>} />
                  <Route path="/vendor" element={<PrivateRoute><Vendor /></PrivateRoute>} />
                  <Route path="/explorer" element={<Explorer />} />
                  <Route path="/explorer/holder/:holderId" element={<HolderHistory />} />

                  {/* Universal SaaS Auto-Trading Platform */}
                  <Route path="/algotrading" element={<PrivateRoute><AlgoTrading /></PrivateRoute>} />

                  {/* Anant Inner Self Guidance — Osho Zen Tarot */}
                  <Route path="/anant-tarot" element={<PrivateRoute><AnantTarot /></PrivateRoute>} />
                  {/* Public Tarot Gallery — SEO + viral sharing */}
                  <Route path="/tarot/gallery" element={<TarotGallery />} />
                  {/* Per-card SEO permalink */}
                  <Route path="/tarot/card/:cardId" element={<TarotCardDetail />} />
                  {/* Public shared reading — viral entry point */}
                  <Route path="/tarot/reading/:readingId" element={<TarotPublicReading />} />

                  {/* Education Hub */}
                  <Route path="/education" element={<PrivateRoute><Education /></PrivateRoute>} />

                  {/* Smart Business Card */}
                  <Route path="/smart-card/manage" element={<PrivateRoute><SmartCardManager /></PrivateRoute>} />
                  <Route path="/smart-card/edit" element={<PrivateRoute><SmartCardEditor /></PrivateRoute>} />
                  <Route path="/smart-card/edit/:id" element={<PrivateRoute><SmartCardEditor /></PrivateRoute>} />

                  {/* Freedom Academy */}
                  <Route path="/academy" element={<PrivateRoute><Academy /></PrivateRoute>} />
                  <Route path="/academy/course/:id" element={<PrivateRoute><AcademyCourse /></PrivateRoute>} />
                  <Route path="/academy/verify/:certId" element={<AcademyCertVerify />} />
                  {/* Anant Vyapar (CRM) */}
                  <Route path="/vyapar" element={<PrivateRoute><AnantVyapar /></PrivateRoute>} />
                  <Route path="/vyapar/qr/:token" element={<VyaparMagicQR />} />
                  {/* Anant Safety QR */}
                  <Route path="/safety-qr" element={<PrivateRoute><AnantSafety /></PrivateRoute>} />
                  <Route path="/apts" element={<PrivateRoute><APTS /></PrivateRoute>} />
                  <Route path="/samadhan" element={<SamadhanCenter />} />
                  <Route path="/samadhan/vision" element={<SamadhanVision />} />
                  <Route path="/samadhan/gallery" element={<SamadhanGallery />} />
                  <Route path="/trial-room" element={<TrialRoom />} />
                  <Route path="/admin/trial-room" element={<TrialRoomAdmin />} />
                  <Route path="/brandmark" element={<PrivateRoute><BrandMark /></PrivateRoute>} />
                  <Route path="/leadgen" element={<PrivateRoute><LeadGen /></PrivateRoute>} />
                  <Route path="/corpus50" element={<PrivateRoute><Corpus50 /></PrivateRoute>} />
                  <Route path="/vault" element={<PrivateRoute><SmartVault /></PrivateRoute>} />
                  <Route path="/anant-yojana" element={<PrivateRoute><AnantYojana /></PrivateRoute>} />
                  <Route path="/anant-khata" element={<PrivateRoute><AnantKhata /></PrivateRoute>} />
                  <Route path="/asn" element={<PrivateRoute><AnantSamachar /></PrivateRoute>} />
                  <Route path="/asn/news/:contentId" element={<PrivateRoute><AnantSamacharArticle /></PrivateRoute>} />
                  <Route path="/health-tracker" element={<PrivateRoute><AnantHealthTracker /></PrivateRoute>} />
                  <Route path="/fieldpro" element={<PrivateRoute><AnantFieldPro /></PrivateRoute>} />
                  <Route path="/citypro" element={<PrivateRoute><AnantCityPro /></PrivateRoute>} />
                  <Route path="/citypro/delivery" element={<PrivateRoute><CityProDelivery /></PrivateRoute>} />
                  <Route path="/citypro/delivery/register" element={<PrivateRoute><CityProDeliveryRegister /></PrivateRoute>} />
                  <Route path="/citypro/showcase" element={<PrivateRoute><CityProShowcase /></PrivateRoute>} />
                  <Route path="/citypro/leaderboard" element={<PrivateRoute><CityProLeaderboard /></PrivateRoute>} />
                  <Route path="/asm" element={<PrivateRoute><AnantSmartMarketing /></PrivateRoute>} />
                  <Route path="/asm/:slug" element={<AnantSmartMarketingPublic />} />
                  <Route path="/vyapaar-master" element={<PrivateRoute><AnantVyapaarMaster /></PrivateRoute>} />
                  <Route path="/avm/:slug" element={<AVMPublicCard />} />
                  <Route path="/flyer-studio" element={<PrivateRoute><AnantSmartFlyer /></PrivateRoute>} />
                  <Route path="/flyer/:slug" element={<FlyerPublicView />} />
                  <Route path="/social-share" element={<PrivateRoute><AnantSocialShare /></PrivateRoute>} />
                  <Route path="/webshop" element={<PrivateRoute><AnantWebShop /></PrivateRoute>} />
                  <Route path="/marketplace" element={<AnantMarketplace />} />
                  <Route path="/marketplace/store/:slug" element={<PublicMarketplaceStore />} />
                  <Route path="/marketplace/store/:slug/videos" element={<PublicStoreVideos />} />
                  {/* Support fix 2026-09-24: keep ?demand= / ?sale= / ?ref= when redirecting, + public shared-post page */}
                  <Route path="/atc" element={<AtcRedirect />} />
                  <Route path="/atc/post/:kind/:id" element={<PublicAtcPost />} />
                  <Route path="/atc/launch-party" element={<LaunchParty />} />
                  <Route path="/atc/founders" element={<FounderWall />} />
                  <Route path="/atc/b2c" element={<PrivateRoute><AtcB2C /></PrivateRoute>} />
                  <Route path="/atc/b2b" element={<PrivateRoute><AtcB2B /></PrivateRoute>} />
                  {/* Legacy alias */}
                  <Route path="/atc/legacy" element={<PrivateRoute><AnantTradeCenter /></PrivateRoute>} />
                  <Route path="/webshop/:slug" element={<PublicWebShop />} />
                  <Route path="/admin/system-auditor" element={<AdminRoute><SystemAuditor /></AdminRoute>} />
                  <Route path="/asn/all" element={<PrivateRoute><AnantSamacharAllNews /></PrivateRoute>} />
                  <Route path="/portfolio-tracker" element={<PrivateRoute><APTS /></PrivateRoute>} />
                  <Route path="/asst" element={<PrivateRoute><ASST /></PrivateRoute>} />
                  <Route path="/system-safety" element={<PrivateRoute><ASST /></PrivateRoute>} />
                <Route path="/safety-qr/scan/:qrId" element={<SafetyQRScan />} />
                <Route path="/leadgen/lp/:slug" element={<LeadGenPublicLP />} />

                  {/* Anant Network Live Feed — Public Social Proof */}
                  <Route path="/anant-live" element={<NetworkLive />} />
                  <Route path="/anant-live/widget" element={<NetworkLiveWidget />} />

                  {/* Unified Admin Console */}
                  <Route path="/anant-admin" element={<AdminRoute><AnantAdminConsole /></AdminRoute>} />
                  <Route path="/admin/anant" element={<AdminRoute><AnantAdminConsole /></AdminRoute>} />
                  <Route path="/admin/ops" element={<AdminRoute><AdminOpsDashboard /></AdminRoute>} />

                  {/* Anant Display Engine — Public Board */}
                  <Route path="/displayboard/:slug" element={<DisplayBoard />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="/admin/tools-analytics" element={<AdminRoute><ToolsAnalytics /></AdminRoute>} />
                  <Route path="/admin/ai-tools" element={<AdminRoute><AIToolsAdmin /></AdminRoute>} />
                  <Route path="/admin/ai-control" element={<AdminRoute><AIControlPanel /></AdminRoute>} />
                  <Route path="/admin/ai-usage" element={<AdminRoute><AIUsageAdmin /></AdminRoute>} />
                  <Route path="/admin/support" element={<AdminRoute><SupportAdmin /></AdminRoute>} />
                  <Route path="/admin/p2p" element={<AdminRoute><P2PAdmin /></AdminRoute>} />
                  <Route path="/admin/franchise" element={<AdminRoute><FranchiseAdmin /></AdminRoute>} />
                  <Route path="/admin/passes" element={<AdminRoute><PassAdmin /></AdminRoute>} />
                  <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
                  <Route path="/admin/courses" element={<AdminRoute><AdminCourses /></AdminRoute>} />
                  <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
                  <Route path="/admin/marketplace-orders" element={<AdminRoute><AdminMarketplaceOrders /></AdminRoute>} />
                  <Route path="/admin/marketplace-reviews" element={<AdminRoute><AdminMarketplaceReviews /></AdminRoute>} />
                  <Route path="/admin/delivery-partners" element={<AdminRoute><AdminDeliveryPartners /></AdminRoute>} />
                  <Route path="/admin/smart-panel" element={<AdminRoute><AdminSmartPanel /></AdminRoute>} />
                  <Route path="/admin/atc" element={<AdminRoute><AdminATC /></AdminRoute>} />
                  <Route path="/admin/commission-ledger" element={<AdminRoute><AdminCommissionLedger /></AdminRoute>} />
                  <Route path="/admin/sub-admins" element={<AdminRoute><AdminSubAdmins /></AdminRoute>} />
                  <Route path="/admin/activation-keys" element={<AdminRoute><AdminActivationKeys /></AdminRoute>} />
                  <Route path="/admin/platform-settings" element={<AdminRoute><AdminPlatformSettings /></AdminRoute>} />
                  <Route path="/admin/user-tools" element={<AdminRoute><AdminUserTools /></AdminRoute>} />
                  <Route path="/admin/auto-approve" element={<AdminRoute><AdminAutoApproveRules /></AdminRoute>} />
                  <Route path="/admin/platform-content" element={<AdminRoute><AdminPlatformContent /></AdminRoute>} />
                  <Route path="/admin/activity-log" element={<AdminRoute><AdminActivityLog /></AdminRoute>} />
                  <Route path="/redeem-key" element={<PrivateRoute><RedeemKey /></PrivateRoute>} />
                  <Route path="/admin/tax-wallet" element={<AdminRoute><AdminTaxWallet /></AdminRoute>} />
                  <Route path="/admin/pages" element={<AdminRoute><AdminPages /></AdminRoute>} />
                  <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
                  <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                  <Route path="/admin/fund-requests" element={<AdminRoute><AdminFundRequests /></AdminRoute>} />
                  <Route path="/admin/testimonials" element={<AdminRoute><AdminTestimonials /></AdminRoute>} />
                  <Route path="/admin/broadcasts" element={<AdminRoute><AdminBroadcasts /></AdminRoute>} />
                  <Route path="/admin/2fa" element={<AdminRoute><Admin2FA /></AdminRoute>} />
                  <Route path="/admin/display-windows" element={<AdminRoute><AdminDisplayWindows /></AdminRoute>} />
                  <Route path="/admin/popups" element={<AdminRoute><AdminPopups /></AdminRoute>} />
                  <Route path="/admin/activation-packs" element={<AdminRoute><AdminActivationPacks /></AdminRoute>} />
                  <Route path="/admin/important-links" element={<AdminRoute><AdminImportantLinks /></AdminRoute>} />
                  <Route path="/admin/system-wallets" element={<AdminRoute><AdminSystemWallets /></AdminRoute>} />
                  <Route path="/admin/custom-pages" element={<AdminRoute><AdminCustomPages /></AdminRoute>} />
                  <Route path="/admin/rank-competitions" element={<AdminRoute><AdminRankCompetitions /></AdminRoute>} />
                  <Route path="/admin/affiliate" element={<AdminRoute><AdminAffiliate /></AdminRoute>} />
                  <Route path="/admin/auction" element={<AdminRoute><AdminAuction /></AdminRoute>} />
                  <Route path="/admin/ledger" element={<AdminRoute><TransactionLedger /></AdminRoute>} />
                  <Route path="/admin/free-tools" element={<AdminRoute><FreeToolsAdmin /></AdminRoute>} />
                  <Route path="/admin/auction/:auctionId/room" element={<AdminRoute><AuctionRoom /></AdminRoute>} />
                  <Route path="/admin/page-builder" element={<AdminRoute><AdminPageBuilder /></AdminRoute>} />
                  <Route path="/admin/page-builder/new" element={<AdminRoute><AdminPageEditor /></AdminRoute>} />
                  <Route path="/admin/page-builder/edit/:pageId" element={<AdminRoute><AdminPageEditor /></AdminRoute>} />
                  <Route path="/admin/page-builder/user-permissions" element={<AdminRoute><AdminUserPagePermissions /></AdminRoute>} />
                  <Route path="/admin/custom-tabs" element={<AdminRoute><AdminCustomTabs /></AdminRoute>} />
                  <Route path="/admin/shop-builder" element={<AdminRoute><AdminShopBuilder /></AdminRoute>} />
                  <Route path="/admin/shop-builder/:shopId" element={<AdminRoute><AdminShopEditor /></AdminRoute>} />
                  <Route path="/admin/template-permissions" element={<AdminRoute><TemplatePermissions /></AdminRoute>} />
                  <Route path="/admin/dvc-templates" element={<AdminRoute><DVCTemplateAdmin /></AdminRoute>} />
                  <Route path="/admin/shop-templates" element={<AdminRoute><ShopTemplateAdmin /></AdminRoute>} />
                  <Route path="/admin/voice-agent" element={<AdminRoute><AdminVoiceAgent /></AdminRoute>} />
                  <Route path="/admin/voice-control" element={<AdminRoute><VoiceControlPanel /></AdminRoute>} />
                  <Route path="/admin/magic-qr" element={<AdminRoute><AdminMagicQR /></AdminRoute>} />
                  <Route path="/admin/whatsapp-bot" element={<AdminRoute><AdminWhatsAppBot /></AdminRoute>} />
                  <Route path="/admin/ranks-rewards" element={<AdminRoute><AdminRanksRewards /></AdminRoute>} />
                  <Route path="/admin/payment-settings" element={<AdminRoute><AdminPaymentSettings /></AdminRoute>} />
                  <Route path="/admin/push-notifications" element={<AdminRoute><AdminPushNotifications /></AdminRoute>} />
                  <Route path="/admin/whatsapp-leads" element={<AdminRoute><AdminWhatsAppLeads /></AdminRoute>} />
                  <Route path="/admin/p2p-coupons" element={<AdminRoute><AdminP2PCoupons /></AdminRoute>} />
                  <Route path="/admin/p2p-settings" element={<AdminRoute><AdminP2PSettings /></AdminRoute>} />
                  
                  {/* Game Module Admin Routes */}
                  <Route path="/admin/games" element={<AdminRoute><GameManager /></AdminRoute>} />
                  <Route path="/admin/game-wallets" element={<AdminRoute><GameSystemWallets /></AdminRoute>} />
                  
                  {/* Biodata Admin */}
                  <Route path="/admin/biodata" element={<AdminRoute><BioDataAdmin /></AdminRoute>} />
                  
                  {/* Hot News Admin */}
                  <Route path="/admin/hot-news" element={<AdminRoute><HotNewsAdmin /></AdminRoute>} />
                  
                  {/* Sankat Mochan Admin */}
                  <Route path="/admin/sankat-mochan" element={<AdminRoute><SankatMochanAdmin /></AdminRoute>} />
                  <Route path="/admin/sankat-vip" element={<AdminRoute><SankatVIPPanel /></AdminRoute>} />
                  <Route path="/admin/anant-feelings" element={<AdminRoute><AnantFeelingsAdmin /></AdminRoute>} />
                  <Route path="/admin/academy-quizzes" element={<AdminRoute><AcademyQuizAdmin /></AdminRoute>} />
                  <Route path="/admin/vyapar" element={<AdminRoute><VyaparAdmin /></AdminRoute>} />
                  <Route path="/admin/safety-qr" element={<AdminRoute><SafetyQRAdmin /></AdminRoute>} />
                  <Route path="/admin/apts" element={<AdminRoute><APTSAdmin /></AdminRoute>} />
                  <Route path="/admin/samadhan" element={<AdminRoute><SamadhanAdmin /></AdminRoute>} />
                  <Route path="/admin/new-tools" element={<AdminRoute><AdminNewTools /></AdminRoute>} />
                  <Route path="/admin/asst" element={<AdminRoute><ASSTAdmin /></AdminRoute>} />
                  
                  {/* Marketing AI Admin */}
                  <Route path="/admin/marketing" element={<AdminRoute><MarketingAdmin /></AdminRoute>} />
                  
                  {/* Share Analytics */}
                  <Route path="/admin/share-analytics" element={<AdminRoute><ShareAnalytics /></AdminRoute>} />
                  
                  {/* SuperTool Admin */}
                  <Route path="/admin/supertool" element={<AdminRoute><SuperToolAdmin /></AdminRoute>} />
                  
                  {/* QR Lead Collector Admin */}
                  <Route path="/admin/qr-leads" element={<AdminRoute><QRLeadAdmin /></AdminRoute>} />

                  {/* AVX Admin */}
                  <Route path="/admin/avx" element={<AdminRoute><AVXAdmin /></AdminRoute>} />
                  <Route path="/admin/tsb" element={<AdminRoute><TSBAdmin /></AdminRoute>} />
                  <Route path="/admin/algotrading" element={<AdminRoute><AlgoTradingAdmin /></AdminRoute>} />
                  <Route path="/admin/anant-tarot" element={<AdminRoute><AnantTarotAdmin /></AdminRoute>} />
                  <Route path="/admin/displayengine" element={<AdminRoute><DisplayEngineAdmin /></AdminRoute>} />
                  
                  {/* Public Shop Pages */}
                  <Route path="/shop/:slug" element={<PublicShop />} />
                  
                  {/* Public Magic QR Review Page */}
                  <Route path="/review/:qrCode" element={<PublicReview />} />
                  
                  {/* Public Biodata View */}
                  <Route path="/biodata/:biodataId" element={<PublicBioData />} />
                  
                  {/* Public Page Builder Pages */}
                  <Route path="/p/:slug" element={<PublicPage />} />

                  {/* Catch-all 404 — MUST be last within Layout */}
                  <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                } />
              </Routes>
              <Toaster position="top-right" />
              <SonnerToaster position="top-right" richColors expand closeButton theme="dark" duration={5000} />
            </BrowserRouter>
          </LanguageProvider>
        </CartProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}

export default App;
